import { ApolloError, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { CREATE_AI_FEEDBACK } from 'shared-components/graphql/AiFeedbackQueries';

// Type for JSON-serializable content that can be stored as feedback
export type SummaryContentData = Record<string, unknown> | string | null;

export interface CreateAiFeedbackInput {
  patientId: string;
  summaryContent?: SummaryContentData;
  page: string;
  category: string;
  comments?: string;
  traceId?: string;
  spanId?: string;
}

export interface UseCreateAiFeedbackReturn {
  createFeedback: (input: CreateAiFeedbackInput) => Promise<{ success: boolean }>;
  loading: boolean;
  error: ApolloError | undefined;
}

export const useCreateAiFeedback = (): UseCreateAiFeedbackReturn => {
  // Create feedback mutation
  const [createFeedbackMutation, { loading, error }] = useMutation(CREATE_AI_FEEDBACK);

  // Create feedback function
  const createFeedback = useCallback(
    async (input: CreateAiFeedbackInput) => {
      const { summaryContent, ...otherInput } = input;

      const variables = {
        ...otherInput,
        summaryContent:
          summaryContent === undefined || summaryContent === null
            ? undefined
            : typeof summaryContent === 'string'
            ? summaryContent
            : JSON.stringify(summaryContent),
      };

      const result = await createFeedbackMutation({ variables });
      return { success: result.data?.createAiFeedback?.success || false };
    },
    [createFeedbackMutation],
  );

  return {
    createFeedback,
    loading,
    error,
  };
};

export default useCreateAiFeedback;
